import { useRef, useState, type FormEvent } from "react";
import { z } from "zod";
import { CheckCircle2, FileText } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { submitLead } from "@/lib/contact.functions";
import { Reveal } from "./Reveal";
import { pricingPlans } from "@/lib/finendra-data";
import { trackFormSubmission } from "@/lib/analytics";

const inputClass =
  "w-full rounded-xl border border-brand-navy/10 bg-background px-4 py-3 text-sm text-brand-navy outline-none transition-colors focus:border-brand-accent";

const proposalSchema = z.object({
  name: z.string().trim().min(1, "Please enter your name.").max(100),
  email: z.string().trim().email("Please enter a valid work email.").max(255),
  company: z.string().trim().max(150).optional(),
  plan: z.string().trim().min(1).max(150),
  hours: z.string().trim().max(50).optional(),
  message: z.string().trim().min(1, "Tell us a little about your books.").max(2000),
});

export function ProposalForm() {
  const renderedAt = useRef(Date.now());
  const send = useServerFn(submitLead);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const form = new FormData(e.currentTarget);
    const parsed = proposalSchema.safeParse({
      name: String(form.get("name") ?? ""),
      email: String(form.get("email") ?? ""),
      company: String(form.get("company") ?? ""),
      plan: String(form.get("plan") ?? ""),
      hours: String(form.get("hours") ?? ""),
      message: String(form.get("message") ?? ""),
    });

    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Please check your details.");
      return;
    }

    const { name, email, company, plan, hours, message } = parsed.data;
    const detail = [
      `Plan of interest: ${plan}`,
      hours ? `Estimated monthly hours: ${hours}` : null,
      "",
      message,
    ]
      .filter((line) => line !== null)
      .join("\n")
      .slice(0, 2000);

    setSubmitting(true);
    const result = await send({
      data: {
        name,
        email,
        company: company || "",
        service: `Proposal request — ${plan}`.slice(0, 150),
        message: detail,
        source: "pricing_proposal",
        website: String(form.get("website") ?? ""),
        renderedAt: renderedAt.current,
      },
    });
    setSubmitting(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    setSent(true);
    trackFormSubmission("pricing_proposal", {
      plan,
      hours: hours || undefined,
    });
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="grid gap-12 rounded-3xl border border-brand-navy/10 bg-background p-6 shadow-xl shadow-brand-navy/5 sm:p-10 lg:grid-cols-[1fr_1.15fr] lg:gap-16">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-navy/15 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-brand-navy/70">
            <FileText className="size-3.5" aria-hidden="true" />
            Proposal
          </span>
          <h2 className="mt-6 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
            Request a proposal
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-brand-navy/60">
            Send us your volumes and current setup and we'll come back with a written proposal:
            recommended role mix, scope, fixed monthly fee, and a start date.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-brand-navy/70">
            {[
              "Response within one business day",
              "Fixed fee — no hourly surprises",
              "No obligation and no long-term lock-in",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle2
                  className="mt-0.5 size-4 shrink-0 text-brand-accent"
                  aria-hidden="true"
                />
                {item}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.08}>
          {sent ? (
            <div className="flex h-full flex-col items-start justify-center rounded-2xl bg-brand-surface p-8">
              <h3 className="font-display text-2xl font-bold text-brand-navy">Request received.</h3>
              <p className="mt-3 text-brand-navy/60">
                We'll send your proposal within one business day. For anything urgent, email
                connect@finendra.com.
              </p>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="mt-6 rounded-xl border border-brand-navy/10 bg-background px-6 py-3 text-sm font-semibold text-brand-navy transition-colors hover:border-brand-navy/25"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="proposal-name"
                    className="mb-2 block text-sm font-medium text-brand-navy"
                  >
                    Full name
                  </label>
                  <input id="proposal-name" name="name" required className={inputClass} />
                </div>
                <div>
                  <label
                    htmlFor="proposal-email"
                    className="mb-2 block text-sm font-medium text-brand-navy"
                  >
                    Work email
                  </label>
                  <input
                    id="proposal-email"
                    name="email"
                    type="email"
                    required
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="proposal-company"
                    className="mb-2 block text-sm font-medium text-brand-navy"
                  >
                    Company
                  </label>
                  <input id="proposal-company" name="company" className={inputClass} />
                </div>
                <div>
                  <label
                    htmlFor="proposal-hours"
                    className="mb-2 block text-sm font-medium text-brand-navy"
                  >
                    Estimated hours / month
                  </label>
                  <input
                    id="proposal-hours"
                    name="hours"
                    inputMode="numeric"
                    placeholder="e.g. 60"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="proposal-plan"
                  className="mb-2 block text-sm font-medium text-brand-navy"
                >
                  Plan of interest
                </label>
                <select
                  id="proposal-plan"
                  name="plan"
                  required
                  defaultValue="Not sure yet"
                  className={inputClass}
                >
                  {pricingPlans.map((plan) => (
                    <option key={plan.name}>{plan.name}</option>
                  ))}
                  <option>Blended team</option>
                  <option>Not sure yet</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="proposal-message"
                  className="mb-2 block text-sm font-medium text-brand-navy"
                >
                  Tell us about your books
                </label>
                <textarea
                  id="proposal-message"
                  name="message"
                  rows={4}
                  required
                  placeholder="Monthly transaction volume, accounting software, entities, and what's currently painful."
                  className={inputClass}
                />
              </div>

              {/* Honeypot — hidden from people, tempting to bots. */}
              <div aria-hidden="true" className="hidden">
                <label htmlFor="proposal-website">Website</label>
                <input
                  id="proposal-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {error && (
                <p role="alert" className="text-sm font-medium text-destructive">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-xl bg-brand-navy px-8 py-4 font-semibold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 hover:bg-brand-accent active:scale-[0.985] active:duration-75 motion-reduce:transform-none select-none disabled:cursor-not-allowed disabled:opacity-60 disabled:transform-none"
              >
                {submitting ? "Sending…" : "Request a proposal"}
              </button>

              <div className="flex items-center gap-2 text-xs text-brand-navy/60">
                <span className="font-semibold text-brand-navy">
                  🔒 Confidential &amp; Protected:
                </span>
                <span>
                  We never share your details. Scoped proposal delivered within 1 business day.
                </span>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </div>
  );
}
